import { Table } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/customTable.scss";
import useColumnResize from "../../hooks/useColumnResize";
const ScheduledChangesTable = ({
  data,
  onSortChange,
  onSelectionChange,
  isLoading = false,
  errorMessage = "",
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: "scheduledTime", direction: "desc" });
  const [selectedId, setSelectedId] = useState(null);
  const { getResizeProps } = useColumnResize();

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    if (typeof onSortChange === "function") {
      onSortChange(newSortConfig);
    }
  };

  const items = Array.isArray(data) ? data : [];
  const emptyStateMessage = isLoading
    ? "Ładowanie…"
    : errorMessage || "Brak danych";
  const emptyStateClassName = isLoading
    ? "text-muted"
    : errorMessage
      ? "text-danger"
      : "";

  const getRowId = (item, index) => {
    return (
      item?.changeId ??
      item?.ChangeId ??
      item?.id ??
      item?.Id ??
      item?.scheduledChangeId ??
      item?.historyId ??
      index
    );
  };

  const isSelectableStatus = (status) => {
    const normalized = String(status ?? "").trim().toLowerCase();
    const allowedStatuses = new Set([
      "approved",
      "pending",
      "rejected",
      "zaplanowane",
      "do akceptacji",
      "odrzucone",
      "odrzucoone",
    ]);

    return allowedStatuses.has(normalized);
  };

  const allRowIds = useMemo(
    () => items.map((item, idx) => getRowId(item, idx)),
    [items]
  );

  const selectableRowIds = useMemo(
    () =>
      items
        .map((item, idx) => ({ id: getRowId(item, idx), status: item?.status }))
        .filter(({ status }) => isSelectableStatus(status))
        .map(({ id }) => id),
    [items]
  );

  useEffect(() => {
    if (typeof onSelectionChange !== "function") return;

    const selected = selectedId
      ? items.filter((item, idx) => getRowId(item, idx) === selectedId)
      : [];
    onSelectionChange(selected);
  }, [selectedId, items]);


  useEffect(() => {
    setSelectedId((prev) =>
      allRowIds.includes(prev) && selectableRowIds.includes(prev) ? prev : null
    );
  }, [allRowIds, selectableRowIds]);

  const getSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "↑" : "↓";
    }
    return "";
  };

  const formatDateTime = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string" || typeof value === "number") return value;
    if (typeof value === "boolean") return value ? "Tak" : "Nie";

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const parseChangeDetailsJson = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(String(value));
    } catch {
      return null;
    }
  };

  const getUpdatesRows = (changeDetailsJson) => {
    const parsed = parseChangeDetailsJson(changeDetailsJson);
    const updates = parsed?.Updates ?? parsed?.updates;

    if (Array.isArray(updates)) {
      return updates.filter((item) => item && typeof item === "object");
    }

    if (updates && typeof updates === "object") {
      return [updates];
    }

    return [];
  };

  const renderKeyValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Tak" : "Nie";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const renderUpdatesPreview = (changeDetailsJson) => {
    const rows = getUpdatesRows(changeDetailsJson);
    if (rows.length === 0) {
      return <span>Brak danych w Updates.</span>;
    }

    const preferredOrder = ["Column", "Value", "OldValue", "NewValue"];

    return rows.map((row, rowIndex) => {
      const keys = Object.keys(row);
      const orderedKeys = [
        ...preferredOrder.filter((key) => keys.includes(key)),
        ...keys.filter((key) => !preferredOrder.includes(key)),
      ];

      return (
        <div
          key={`upd-${rowIndex}`}
          style={{
            marginBottom: rowIndex < rows.length - 1 ? 10 : 0,
            paddingBottom: rowIndex < rows.length - 1 ? 8 : 0,
            borderBottom: rowIndex < rows.length - 1 ? "1px dashed rgba(0,0,0,0.2)" : "none",
          }}
        >
          {orderedKeys.map((key) => (
            <div key={`upd-${rowIndex}-${key}`} style={{ marginBottom: 2 }}>
              <strong>{key}:</strong> {renderKeyValue(row[key])}
            </div>
          ))}
        </div>
      );
    });
  };

  const statusLabels = {
    Approved: "Zaplanowane",
    Pending: "Do akceptacji",
    Rejected: "Odrzucone",
    Withdrawn: "Wycofane",
    Executed: "Wykonane",
    Queued: "W trakcie wykonywania",
    Failed: "Wystąpił błąd",
  };

  const formatStatus = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return statusLabels[value] ?? formatValue(value);
  };

  const handleRowClick = (item, index, event) => {
    const interactiveEl = event.target.closest("input, button, a, summary, details, label");
    if (interactiveEl) return;

    const id = getRowId(item, index);
    if (id === null || id === undefined || id === "") return;

    navigate(`/scheduled-changes/${id}`);
  };

  return (
    <div>
      <Table
        striped
        bordered
        hover
        size="sm"
        className="custom-table scheduled-changes-table"
      >
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <span aria-hidden="true" />
            </th>
            <th onClick={() => requestSort("createdAt")} {...getResizeProps()}>
              Data dodania {getSortArrow("createdAt")}
            </th>
            <th onClick={() => requestSort("scheduledTime")} {...getResizeProps()}>
              Data zaplanowania {getSortArrow("scheduledTime")}
            </th>
            <th onClick={() => requestSort("rollbackScheduledTime")} {...getResizeProps()}>
              Data wycofania {getSortArrow("rollbackScheduledTime")}
            </th>
            <th onClick={() => requestSort("status")} {...getResizeProps()}>
              Status {getSortArrow("status")}
            </th>
            <th onClick={() => requestSort("terminalDr")} {...getResizeProps()}>
              DR {getSortArrow("terminalDr")}
            </th>
            <th onClick={() => requestSort("terminalEx")} {...getResizeProps()}>
              EX {getSortArrow("terminalEx")}
            </th>
            <th onClick={() => requestSort("requestedBy")} {...getResizeProps()}>
              Użytkownik dodający {getSortArrow("requestedBy")}
            </th>
            <th onClick={() => requestSort("approvedBy")} {...getResizeProps()}>
              Użytkownik akceptujący {getSortArrow("approvedBy")}
            </th>
            <th onClick={() => requestSort("changes")} {...getResizeProps()}>
              Zmieniane wartości {getSortArrow("changes")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item, index) => (
              <tr
                key={getRowId(item, index)}
                onClick={(e) => handleRowClick(item, index, e)}
                style={{ cursor: "pointer" }}
              >
                <td data-label="Wybór">
                  <input
                    type="checkbox"
                    aria-label="Zaznacz wiersz"
                    title={
                      isSelectableStatus(item?.status)
                        ? "Zaznacz wiersz"
                        : "Zaznaczenie dostępne tylko dla statusów: Zaplanowane, Do akceptacji, Odrzucone"
                    }
                    disabled={!isSelectableStatus(item?.status)}
                    checked={selectedId === getRowId(item, index)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      if (!isSelectableStatus(item?.status)) return;
                      const checked = e.target.checked;
                      const id = getRowId(item, index);
                      setSelectedId(checked ? id : null);
                    }}
                  />
                </td>
                <td data-label="Data dodania">{formatDateTime(item.createdAt)}</td>
                <td data-label="Data zaplanowania">{formatDateTime(item.scheduledTime)}</td>
                <td data-label="Data wycofania">{formatDateTime(item.rollbackScheduledTime)}</td>
                <td data-label="Status">{formatStatus(item.status)}</td>
                <td data-label="DR">{formatValue(item.terminalDr)}</td>
                <td data-label="EX">{formatValue(item.terminalEx)}</td>
                <td data-label="Użytkownik dodający">{formatValue(item.requestedBy)}</td>
                <td data-label="Użytkownik akceptujący">{formatValue(item.approvedBy)}</td>
                <td data-label="Zmieniane wartości" style={{ maxWidth: 360 }}>
                  <details>
                    <summary>Kliknij aby rozwinąć</summary>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        marginTop: 6,
                      }}
                    >
                      {renderUpdatesPreview(item.changeDetailsJson)}
                    </div>
                  </details>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan="10"
                className={`text-center scheduled-changes-table__empty ${emptyStateClassName}`.trim()}
              >
                {emptyStateMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ScheduledChangesTable;
